import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { useToast } from "@chakra-ui/toast";
import { commonSelector } from "../../store/slices/common/commonSlice";
import { initiateOrderRefund, getOrderRefunds } from "../../services/apiService";
import {
  getCurrencySymbol,
  getGatewayDisplayName,
} from "../../helper/_commonHelper";

Modal.setAppElement("body");

const formatTimestamp = (date) =>
  `${date.toDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;

const money = (n) => Number(n || 0).toFixed(2);

function RefundConfirmation({ open, close, order, payment, onSuccess }) {
  const toast = useToast();
  const { userProfile } = useSelector(commonSelector);
  const hotelCurrencyCode = userProfile?.hotel?.country?.currency_code || "";
  const currencySymbol = getCurrencySymbol(hotelCurrencyCode);

  const paidAmount = useMemo(
    () => (payment?.amount != null ? Number(payment.amount) : 0),
    [payment]
  );

  const [refunderName, setRefunderName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [timestamp, setTimestamp] = useState(new Date());
  const [acknowledged, setAcknowledged] = useState(false);

  // Prior refunds against this payment. Success and pending both count as
  // spoken-for: a pending QPAY refund is money in flight, not money available.
  const [priorRefunds, setPriorRefunds] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [amountInput, setAmountInput] = useState("");

  const alreadyRefunded = useMemo(
    () =>
      priorRefunds
        .filter((r) => ["success", "pending"].includes(String(r.status)))
        .reduce((s, r) => s + Number(r.amount || 0), 0),
    [priorRefunds]
  );
  const remaining = Math.max(0, paidAmount - alreadyRefunded);
  const fullyRefunded = historyLoaded && !historyFailed && remaining <= 0.001;

  const amount = Number(amountInput);
  const amountValid =
    amountInput !== "" && !Number.isNaN(amount) && amount > 0 &&
    amount <= remaining + 0.001;
  const isPartial = amountValid && amount < remaining - 0.001;

  useEffect(() => {
    if (!open) return;
    setRefunderName(userProfile?.name || "");
    setTimestamp(new Date());
    setAcknowledged(false);
    setHistoryLoaded(false);
    setHistoryFailed(false);
    setPriorRefunds([]);
    setAmountInput("");

    let cancelled = false;
    getOrderRefunds(order.id)
      .then((res) => {
        if (cancelled) return;
        const refunds = res?.data?.data?.refunds || [];
        setPriorRefunds(refunds);
        const used = refunds
          .filter((r) => ["success", "pending"].includes(String(r.status)))
          .reduce((s, r) => s + Number(r.amount || 0), 0);
        setAmountInput(money(Math.max(0, Number(payment?.amount || 0) - used)));
      })
      .catch(() => {
        if (cancelled) return;
        // The server enforces the same cap, so proceeding with the full paid
        // amount is safe - an over-refund gets rejected there. But say so.
        setHistoryFailed(true);
        setAmountInput(money(payment?.amount));
      })
      .finally(() => {
        if (!cancelled) setHistoryLoaded(true);
      });

    const interval = setInterval(() => setTimestamp(new Date()), 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, userProfile, order, payment]);

  const handleSubmit = async () => {
    if (!refunderName?.trim()) {
      toast({
        title: "Refunder name required",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!amountValid) {
      toast({
        title: "Invalid refund amount",
        description: `Enter an amount between 0.01 and ${money(remaining)} (${currencySymbol} ${money(alreadyRefunded)} already refunded).`,
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    if (!acknowledged) {
      toast({
        title: "Please confirm the refund disclaimer before proceeding",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    let response;
    try {
      response = await initiateOrderRefund(order.id, {
        amount: money(amount),
        refundedBy: refunderName.trim(),
      });
    } catch (error) {
      const resp = error?.response;
      const code = resp?.data?.data?.code || resp?.data?.code;
      const message =
        resp?.data?.message ||
        error?.message ||
        "Failed to initiate refund. Please try again.";
      toast({
        title: code ? `Refund failed (${code})` : "Refund failed",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setSubmitting(false);
      return;
    }

    // HTTP 200 - refund accepted. Post-success UI work runs OUTSIDE the
    // try/catch so a render/reload hiccup is never reported as a refund
    // failure. submitting stays true until the page reloads.
    const refund = response?.data?.data?.refund;
    const status = refund?.status;
    toast({
      title:
        status === "success"
          ? "Refund processed successfully"
          : status === "pending"
          ? "Refund initiated - pending bank confirmation"
          : "Refund initiated",
      description: refund?.gateway_status_message || response?.data?.message,
      status: status === "success" ? "success" : "info",
      duration: 4000,
      isClosable: true,
    });
    onSuccess && onSuccess(refund);
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={submitting ? undefined : close}
      className={"modal-dialog modal-md refund-confirm-modal"}
      overlayClassName={"modal-backdrop overflow-auto"}
      contentLabel="Refund Confirmation"
    >
      <div className="modal-content" aria-modal="true">
        <div className="modal-header">
          <div className="title">Confirm Refund</div>
          <button
            className="btn btn-icon"
            onClick={close}
            disabled={submitting}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="details-div f-d-r">
            <div className="modal-details-sec">
              <label className="details-label">Order ID:</label>
              <div className="details-value">#{order?.id}</div>
            </div>
            <div className="modal-details-sec">
              <label className="details-label">Gateway:</label>
              <div className="details-value">
                {getGatewayDisplayName(payment?.gateway || "qpay")}
              </div>
            </div>
            <div className="modal-details-sec">
              <label className="details-label">Paid Amount:</label>
              <div className="details-value">
                {currencySymbol} {money(paidAmount)}
              </div>
            </div>
            <div className="modal-details-sec">
              <label className="details-label">Refundable:</label>
              <div className="details-value">
                {historyLoaded ? `${currencySymbol} ${money(remaining)}` : "…"}
              </div>
            </div>
          </div>

          {priorRefunds.length > 0 && (
            <div className="details-div">
              <label className="details-label">Previous refunds on this payment</label>
              {priorRefunds.map((r) => (
                <div className="details-value" key={r.id}>
                  {currencySymbol} {money(r.amount)} — {String(r.status)}
                  {r.initiated_at ? ` — ${String(r.initiated_at).slice(0, 16).replace("T", " ")}` : ""}
                  {r.refunded_by ? ` — by ${r.refunded_by}` : ""}
                </div>
              ))}
            </div>
          )}

          {historyFailed && (
            <div className="refund-disclaimer-text">
              <i className="fa fa-exclamation-triangle"></i>{" "}
              <span>
                Could not load previous refunds. The server still enforces the
                refundable limit, so an over-refund will be rejected.
              </span>
            </div>
          )}

          {fullyRefunded ? (
            <div className="refund-disclaimer-text">
              <i className="fa fa-check-circle"></i>{" "}
              <span>
                This payment is already fully refunded ({currencySymbol}{" "}
                {money(alreadyRefunded)} of {currencySymbol} {money(paidAmount)}
                ). Nothing left to refund.
              </span>
            </div>
          ) : (
            <div className="details-div f-d-r">
              <div className="modal-details-sec">
                <label className="details-label">Refunder Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={refunderName}
                  onChange={(e) => setRefunderName(e.target.value)}
                  disabled={submitting}
                  placeholder="Enter refunder name"
                />
              </div>
              <div className="modal-details-sec">
                <label className="details-label">
                  Refund Amount ({currencySymbol}) *
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={amountInput}
                  min="0.01"
                  step="0.01"
                  max={money(remaining)}
                  onChange={(e) => setAmountInput(e.target.value)}
                  disabled={submitting || !historyLoaded}
                  placeholder={money(remaining)}
                />
                {amountInput !== "" && !amountValid && (
                  <div className="details-value refund-amount-frozen">
                    Must be between 0.01 and {money(remaining)}
                  </div>
                )}
                {isPartial && (
                  <div className="details-value">
                    Partial refund — {currencySymbol} {money(remaining - amount)}{" "}
                    will stay charged.
                  </div>
                )}
              </div>
              <div className="modal-details-sec">
                <label className="details-label">Timestamp</label>
                <div className="details-value">{formatTimestamp(timestamp)}</div>
              </div>
            </div>
          )}

          {!fullyRefunded && (
            <div className="refund-disclaimer">
              <div className="refund-disclaimer-text">
                <i className="fa fa-exclamation-triangle"></i>
                <span>
                  <span className="asterisk">*</span>{" "}
                  {String(payment?.gateway || "").toLowerCase() === "cybersource"
                    ? "The refund will be processed back to the same card used for the original payment."
                    : `The refund will be processed to the same account used for the original ${getGatewayDisplayName(
                        payment?.gateway || "qpay"
                      )} payment.`}{" "}
                  <strong>This action cannot be undone.</strong>
                </span>
              </div>
              <label className="refund-disclaimer-ack">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  disabled={submitting}
                />
                <span>
                  I confirm refunding {currencySymbol}{" "}
                  {amountValid ? money(amount) : "--"} to this guest{" "}
                  <span className="asterisk">*</span>
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            className="btn btn-default"
            onClick={close}
            disabled={submitting}
          >
            {fullyRefunded ? "Close" : "Cancel"}
          </button>
          {!fullyRefunded && (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={
                submitting || !acknowledged || !amountValid || !historyLoaded
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin"></i> Processing...
                </>
              ) : (
                `Refund ${currencySymbol} ${amountValid ? money(amount) : "--"}`
              )}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default RefundConfirmation;
